import type { WidgetUpdate } from 'lib-shared-legacy-types';
import { DeepNonNullable } from 'ts-essentials';

import { ajaxSend } from 'api/api';

import type {
  WidgetCreatePayloadT,
  WidgetsCreateResponseT,
  WidgetsGetItemResponseT,
  WidgetsGetItemsResponseT,
  WidgetsQueryParams,
  WidgetsRemoveResponseT,
  WidgetsUpdateResponseT,
} from './types';

const WIDGET_API_BASE_V1 = '/v1/widgets';
const WIDGET_API_BASE_V2 = '/v2/widgets';

class WidgetsApi {
  /**
   * Creates a new widget.
   *
   * @param {WidgetCreatePayloadT} data The create widget data object.
   */
  public createWidget(data: WidgetCreatePayloadT) {
    return ajaxSend<WidgetsCreateResponseT>({
      data,
      method: 'POST',
      path: WIDGET_API_BASE_V1,
    });
  }

  /**
   * Gets a single widget.
   *
   * @param {string} widgetId The id of the widget to be retrieved
   */
  public getWidget(widgetId: string) {
    return ajaxSend<WidgetsGetItemResponseT>({
      path: `${WIDGET_API_BASE_V1}/${widgetId}`,
    });
  }

  /**
   * Gets widgets.
   *
   * @param {WidgetsQueryParams} [params={}] The get widgets query params
   *   object. Default is `{}`
   */
  public getWidgets(params: DeepNonNullable<WidgetsQueryParams> = {}) {
    return ajaxSend<WidgetsGetItemsResponseT>({
      params,
      path: `${WIDGET_API_BASE_V2}`,
    });
  }

  /**
   * Updates widget.
   *
   * @param {string} widgetId The id of the widget to be updated.
   * @param {WidgetUpdate} payload The payload used to update the widget.
   */
  public updateWidget(widgetId: string, payload: WidgetUpdate) {
    return ajaxSend<WidgetsUpdateResponseT>({
      data: payload,
      method: 'PATCH',
      path: `${WIDGET_API_BASE_V1}/${widgetId}`,
    });
  }

  /**
   * Removes widget.
   *
   * @param {string} widgetId The id of the widget to be removed.
   */
  public removeWidget(widgetId: string) {
    return ajaxSend<WidgetsRemoveResponseT>({
      method: 'DELETE',
      path: `${WIDGET_API_BASE_V1}/${widgetId}`,
    });
  }
}

export const widgetsApi = new WidgetsApi();
